import React from 'react';

export default function EntitiesExplanation() {
    const [selectedSection, setSelectedSection] = React.useState('definition');

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                    <h2 className="text-2xl font-bold mb-4">Entities</h2>
                    
                    <div className="mb-4 flex space-x-2">
                        {['definition', 'structure', 'examples', 'implementation'].map(section => (
                            <button
                                key={section}
                                onClick={() => setSelectedSection(section)}
                                className={`px-4 py-2 rounded ${
                                    selectedSection === section
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-100'
                                }`}
                            >
                                {section.charAt(0).toUpperCase() + section.slice(1)}
                            </button>
                        ))}
                    </div>

                    {selectedSection === 'definition' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Qu'est-ce qu'une Entity ?</h3>
                            <ul className="space-y-2 list-disc list-inside text-gray-700">
                                <li>Objet métier avec une identité unique</li>
                                <li>Mutable - peut changer d'état au fil du temps</li>
                                <li>Égalité basée sur l'identité, pas sur les attributs</li>
                                <li>Encapsule la logique métier et les règles de validation</li>
                                <li>Maintient l'intégrité de ses données</li>
                            </ul>
                        </div>
                    )}

                    {selectedSection === 'structure' && (
                        <div className="bg-white rounded-lg p-4 shadow">
                            <h3 className="text-lg font-semibold mb-2">Structure d'une Entity</h3>
                            <pre className="bg-gray-50 p-4 rounded text-sm">
{`@Entity
public class Payment extends BaseEntity {
    @Id
    private final UUID id;

    @NotNull
    private Amount amount;

    @NotNull
    private PaymentStatus status;

    @NotNull
    @Embedded
    private Party debtor;

    @NotNull
    @Embedded
    private Party creditor;

    @NotNull
    private LocalDate executionDate;

    @ElementCollection
    private List<PaymentEvent> events = new ArrayList<>();

    // Constructeur privé - utiliser le Builder
    private Payment(PaymentBuilder builder) {
        this.id = UUID.randomUUID();
        this.amount = builder.amount;
        this.debtor = builder.debtor;
        this.creditor = builder.creditor;
        this.executionDate = builder.executionDate;
        this.status = PaymentStatus.CREATED;
        
        validateState();
    }

    // Méthodes métier
    public void process() {
        validateCanProcess();
        status = PaymentStatus.PROCESSING;
        addEvent(new PaymentProcessedEvent(this.id));
    }

    public void complete() {
        validateCanComplete();
        status = PaymentStatus.COMPLETED;
        addEvent(new PaymentCompletedEvent(this.id));
    }

    // Validations
    private void validateState() {
        List<ValidationError> errors = new ArrayList<>();
        
        if (amount == null) {
            errors.add(new ValidationError("Amount is required"));
        }
        
        if (debtor == null) {
            errors.add(new ValidationError("Debtor is required"));
        }
        
        if (creditor == null) {
            errors.add(new ValidationError("Creditor is required"));
        }
        
        if (!errors.isEmpty()) {
            throw new ValidationException(errors);
        }
    }

    // Builder pattern
    public static PaymentBuilder builder() {
        return new PaymentBuilder();
    }
}`}
                            </pre>
                        </div>
                    )}

                    {selectedSection === 'examples' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Exemples d'Entities</h3>
                            
                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">DirectDebit Entity</h4>
                                <pre className="text-xs mt-2">
{`@Entity
public class DirectDebit extends Payment {
    @NotNull
    private final MandateReference mandateRef;
    
    @NotNull
    private SequenceType sequenceType;
    
    @NotNull
    private LocalDate collectionDate;

    public void collect() {
        validateCanCollect();
        validateMandateValidity();
        validateCollectionDate();
        
        status = PaymentStatus.COLLECTING;
        addEvent(new DirectDebitCollectionEvent(this.id));
    }

    private void validateCanCollect() {
        if (!Status.READY_FOR_COLLECTION.equals(status)) {
            throw new BusinessException(
                "Direct debit must be ready for collection"
            );
        }
    }

    private void validateMandateValidity() {
        if (!mandate.isValidOn(collectionDate)) {
            throw new BusinessException("Mandate is not valid");
        }
    }
}`}
                                </pre>
                            </div>

                            <div className="p-2 bg-gray-50 rounded">
                                <h4 className="font-medium text-blue-600">Mandate Entity</h4>
                                <pre className="text-xs mt-2">
{`@Entity
public class Mandate extends BaseEntity {
    @NotNull
    private final MandateReference reference;
    
    @NotNull
    private MandateStatus status;
    
    @NotNull
    @Embedded
    private Party creditor;
    
    @NotNull
    @Embedded
    private Party debtor;
    
    @NotNull
    private LocalDate startDate;
    
    private LocalDate endDate;
    
    @ElementCollection
    private List<MandateAmendment> amendments = new ArrayList<>();

    public void amend(MandateAmendment amendment) {
        validateCanAmend();
        amendments.add(amendment);
        applyAmendment(amendment);
        addEvent(new MandateAmendedEvent(this.reference));
    }

    public void revoke() {
        validateCanRevoke();
        status = MandateStatus.REVOKED;
        endDate = LocalDate.now();
        addEvent(new MandateRevokedEvent(this.reference));
    }

    public boolean isValidOn(LocalDate date) {
        return status == MandateStatus.ACTIVE &&
               !date.isBefore(startDate) &&
               (endDate == null || !date.isAfter(endDate));
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}

                    {selectedSection === 'implementation' && (
                        <div className="bg-white rounded-lg p-4 shadow space-y-4">
                            <h3 className="text-lg font-semibold mb-2">Patterns d'Implémentation</h3>
                            
                            <div className="space-y-2">
                                <h4 className="font-medium">1. Agrégat Root</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Entity
public class PaymentBatch extends AggregateRoot {
    @Id
    private final UUID batchId;
    
    @OneToMany(cascade = CascadeType.ALL)
    private List<Payment> payments = new ArrayList<>();
    
    private BatchStatus status;

    public void addPayment(Payment payment) {
        validateCanAddPayment();
        payments.add(payment);
        updateTotals();
    }

    public void process() {
        validateCanProcess();
        status = BatchStatus.PROCESSING;
        payments.forEach(Payment::process);
        addEvent(new BatchProcessingStartedEvent(this.batchId));
    }
}`}
                                </pre>
                            </div>

                            <div className="space-y-2">
                                <h4 className="font-medium">2. Entity avec Workflow</h4>
                                <pre className="bg-gray-50 p-2 rounded text-xs">
{`@Entity
public class PaymentWithWorkflow extends Payment {
    @NotNull
    private WorkflowStatus workflowStatus;
    
    @ElementCollection
    private List<WorkflowTransition> transitions = new ArrayList<>();

    public void transit(String event) {
        WorkflowTransition transition = 
            WorkflowDefinition.validateTransition(
                workflowStatus, event
            );
        
        executeTransition(transition);
        transitions.add(transition);
        addEvent(new WorkflowTransitionedEvent(
            this.id, 
            transition
        ));
    }
}`}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white rounded-lg p-4 shadow">
                    <h3 className="text-xl font-bold mb-4">Caractéristiques Clés</h3>
                    
                    <div className="space-y-4">
                        <div className="border-l-4 border-blue-500 pl-4">
                            <h4 className="font-semibold">1. Identité Unique</h4>
                            <p className="text-sm text-gray-600">
                                Chaque entity a un identifiant unique qui la distingue, même si ses attributs changent.
                            </p>
                        </div>

                        <div className="border-l-4 border-green-500 pl-4">
                            <h4 className="font-semibold">2. État Mutable</h4>
                            <p className="text-sm text-gray-600">
                                Peut changer d'état au fil du temps tout en conservant son identité.
                            </p>
                        </div>

                        <div className="border-l-4 border-purple-500 pl-4">
                            <h4 className="font-semibold">3. Cohérence des Données</h4>
                            <p className="text-sm text-gray-600">
                                Maintient l'intégrité et la cohérence de ses données à travers ses changements d'état.
                            </p>
                        </div>

                        <div className="border-l-4 border-yellow-500 pl-4">
                            <h4 className="font-semibold">4. Logique Métier</h4>
                            <p className="text-sm text-gray-600">
                                Encapsule les règles métier et les comportements spécifiques à l'entité.
                            </p>
                        </div>

                        <div className="border-l-4 border-red-500 pl-4">
                            <h4 className="font-semibold">5. Traçabilité</h4>
                            <p className="text-sm text-gray-600">
                                Garde une trace des changements d'état via des événements métier.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}